import React, { useEffect, useState } from 'react';
import { Bell, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
};

/** Opt-in notifications push pour le coach — alerte quand un client termine
 * l'intégralité d'un programme (dernière séance loguée). */
export const AdminPushOptIn = ({ csrfToken }) => {
  const [status, setStatus] = useState('checking'); // checking|unsupported|denied|off|on|loading|error

  const fetchWithCsrf = (url, options = {}) => fetch(url, {
    ...options, credentials: 'include',
    headers: { ...options.headers, 'X-CSRF-Token': csrfToken },
  });

  useEffect(() => {
    const check = async () => {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || typeof Notification === 'undefined') {
        setStatus('unsupported');
        return;
      }
      if (Notification.permission === 'denied') { setStatus('denied'); return; }
      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setStatus(sub ? 'on' : 'off');
      } catch { setStatus('off'); }
    };
    check();
  }, []);

  const enable = async () => {
    setStatus('loading');
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setStatus('denied'); return; }
      const reg = await navigator.serviceWorker.ready;
      const keyRes = await fetch(`${BACKEND_URL}/api/admin/push/public-key`, { credentials: 'include' });
      const { public_key } = await keyRes.json();
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(public_key),
        });
      }
      await fetchWithCsrf(`${BACKEND_URL}/api/admin/push/subscribe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subscription: sub.toJSON() }),
      });
      setStatus('on');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'checking' || status === 'unsupported') return null;

  if (status === 'on') {
    return (
      <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-emerald-400 text-sm font-semibold" data-testid="admin-push-status-on">
        <Check className="w-4 h-4" /> Alertes activées
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <p className="text-xs text-zinc-500 px-2" data-testid="admin-push-status-denied">
        Notifications bloquées (réglages navigateur)
      </p>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={enable}
        disabled={status === 'loading'}
        className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-300 rounded-xl text-sm transition-colors disabled:opacity-60"
        data-testid="admin-enable-push-btn"
      >
        <Bell className="w-4 h-4" /> {status === 'loading' ? 'Activation…' : 'Activer les notifications'}
      </button>
      {status === 'error' && (
        <span className="text-xs text-red-400" data-testid="admin-push-status-error">Échec de l'activation, réessayez.</span>
      )}
    </div>
  );
};

export default AdminPushOptIn;
